/* This FaqTemplate requires Tailwind CSS v2.0+ */
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import MarkdownBlock from './Chat/MarkdownBlock';

const faqs = [
  {
    question: 'Do I own the rights to the generated images?',
    answer:
      'Yes, like with all outputs from our AI, the person who created them owns the output. You can use the generated images for any purpose, including commercial use.',
  },
  {
    question: 'What AI models are supported?',
    answer: `We support 40+ image generation models including:
- OpenAI GPT Image 1.5
- Nano Banana Pro
- OpenAI GPT Image 2.5 (Flare & Sunburst)
- OpenAI GPT Image 2
- Midjourney V6 & V7
- FLUX.2 (Max, Flex, Pro, Dev) & FLUX 1.1 Pro Ultra
- Stable Diffusion 3.5 & SDXL
- Ideogram v3
- Recraft v3
- Seedream 4.5
- Grok
- And many more

We regularly add new models as they become available.`,
  },
  {
    question: 'Can the AI reliably render text?',
    answer: `Yes, many of our models can render text reliably, including Ideogram v3, Recraft v3, FLUX models, Nano Banana Pro, and OpenAI GPT Image 2. These models excel at producing intricate details, including text, hands, and faces. However, there are some limitations — the AI can struggle with text that is very long, overly complex, or contains many special characters. Tips for better text rendering:
- Place the text instruction at the **beginning** of your prompt
- Keep the text short and simple
- Try Ideogram v3 or Recraft v3 for the best text rendering results

![AI generated image with text: AI is easy-peasy](https://pbs.twimg.com/media/F-9Cl-magAAc-pK?format=jpg&name=small)`,
  },
  {
    question: 'Is it free to use? How do credits work?',
    answer: `Yes, you can start for free! Our Free plan includes 8 image generations per month at no cost — no credit card required.

Each image generation costs credits, which vary by model and quality:
- **Budget models** (SDXL, Z-Image Turbo): 0.1–0.25 credits
- **Standard models** (most models): 2 credits
- **Premium models** (Ideogram v3, FLUX 1.1 Pro Ultra): 3–5 credits
- **High quality mode** (OpenAI GPT Image 1.5, OpenAI GPT Image 2 / 2.5 High): up to 11 credits

Paid plans offer significantly more generations per month, and you can also purchase additional credit packs if you need more.`,
  },
  {
    question: 'What image sizes and aspect ratios are supported?',
    answer: `We support a wide range of aspect ratios and resolutions:

**Aspect ratios:** 1:1 (square), 16:9, 9:16, 3:2, 2:3, 4:3, 3:4, 4:5, 5:4, 21:9, and 9:21.

**Resolutions:** Most models support 1K (1024×1024) and 2K resolution. Some models like Seedream 4.5 and Nano Banana Pro support up to 4K resolution. Higher resolutions may cost additional credits.

The available aspect ratios and resolutions depend on the model you choose — the options are shown automatically when you select a model.`,
  },
  {
    question: 'How do I write better prompts?',
    answer: `Here are some tips to get the best results:
- **Be specific and descriptive** — instead of "a cat", try "a fluffy orange tabby cat sitting on a windowsill at sunset, warm golden lighting"
- **Mention the style you want** — e.g. "oil painting style", "photorealistic", "minimalist illustration"
- **Use our 260+ style presets** — browse styles like Oil Painting, Anime, Pixar, Logo, Product Design, and many more to instantly apply a professional look
- **Put the most important elements first** in your prompt
- **Specify lighting, mood, and composition** for more control
- **Try different models** — each model has its own strengths and aesthetic`,
  },
  {
    question: 'Which model should I choose?',
    answer: `With 40+ models, here's a quick guide:

**For photorealism:** Nano Banana Pro, FLUX.2 models, Seedream 4.5
**For artistic styles:** Ideogram v3, Recraft v3, Dreamina 3.1
**For text in images:** Ideogram v3, Recraft v3, OpenAI GPT Image 2.5, OpenAI GPT Image 2, OpenAI GPT Image 1.5
**For speed:** Z-Image Turbo, FLUX.2 [dev]
**For maximum quality:** FLUX.2 [max], Nano Banana Pro, OpenAI GPT Image 1.5 (high quality)
**On a budget:** Stable Diffusion XL (0.25 credits), Z-Image Turbo (0.1 credits), MiniMax Image 01 (0.5 credits)

Not sure? Start with FLUX.2 [pro] or Seedream 4.5 for a great balance of quality, speed, and cost.`,
  },
  {
    question: 'Can I edit existing images, not just generate new ones?',
    answer: `Yes! You can edit any generated image directly using two editing modes:
- **Prompt editing** — describe the changes you want in text (e.g. "change the background to a beach") and the AI applies them instantly
- **Visual annotation** — click directly on the image to place markers and describe what to change at each point, giving you precise control over specific areas

The image editor supports 15+ AI models, keeps a full edit history so you can go back to any version, and lets you upload reference images for more control.

We also offer standalone editing tools:
- **Remove Background** — instantly remove the background from any image
- **Replace Background** — swap the background with a new scene
- **Colorize** — restore color to black & white photos
- **Style Transfer** — apply artistic styles like Studio Ghibli, Oil Painting, Watercolor, Cartoon, Line Drawing, and more
- **Sketch to Image** — turn a rough sketch into a polished image
- **Creative Upscale** — enhance low-resolution images to higher quality
- **Character Reference** — maintain consistent characters across multiple images`,
  },
  {
    question: 'Are my images and prompts private?',
    answer:
      'Yes, your images and prompts are private by default. We do not share your generated images with other users, and your prompts are not visible to anyone else. Your images are stored securely in your account so you can access them anytime from your image history.',
  },
  {
    question: 'What content is not allowed?',
    answer:
      'We use automated content moderation to ensure our platform is used responsibly. Content that violates our policies — including harmful, illegal, or explicit content involving minors — is not permitted. Prompts are checked before generation, and repeated policy violations may result in account restrictions. If your prompt is rejected, try rephrasing it to remove potentially flagged content.',
  },
  {
    question: 'How long does image generation take?',
    answer:
      'Most images are generated in just a few seconds. Speed varies by model — fast models like Z-Image Turbo and FLUX.2 [dev] generate images almost instantly, while premium models like FLUX.2 [max] or Midjourney may take 15–60 seconds for higher quality results. Higher resolutions (2K, 4K) generally take slightly longer than standard 1K resolution.',
  },
];

export default function ImagesFaq() {
  const { t } = useLocale('common');

  return (
    <div className='bg-gray-50 dark:bg-slate-900'>
      <div className='mx-auto max-w-7xl py-12 px-4 sm:py-16 sm:px-6 lg:px-8'>
        <div
          className='mx-auto max-w-3xl divide-y-2 divide-gray-200 dark:divide-slate-600'
          itemScope
          itemType='https://schema.org/FAQPage'
        >
          <h2 className='text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-slate-200'>
            {t('Frequently asked questions')}
          </h2>
          <dl className='mt-6 space-y-6 divide-y divide-gray-200 dark:divide-slate-600'>
            {faqs.map((faq) => (
              <Disclosure
                as='div'
                key={faq.question}
                className='pt-6'
                itemScope
                itemProp='mainEntity'
                itemType='https://schema.org/Question'
              >
                {({ open }) => (
                  <>
                    <dt className='text-lg'>
                      <Disclosure.Button className='flex w-full items-start justify-between text-left text-gray-400 dark:text-slate-400'>
                        <span
                          className='font-medium text-gray-900 dark:text-slate-200'
                          itemProp='name'
                        >
                          {faq.question}
                        </span>
                        <span className='ml-6 flex h-7 items-center'>
                          <ChevronDownIcon
                            className={clsxm(
                              open ? '-rotate-180' : 'rotate-0',
                              'h-6 w-6 transform',
                            )}
                            aria-hidden='true'
                          />
                        </span>
                      </Disclosure.Button>
                    </dt>
                    <Disclosure.Panel
                      as='dd'
                      className='mt-2 pr-12'
                      unmount={false}
                      itemScope
                      itemProp='acceptedAnswer'
                      itemType='https://schema.org/Answer'
                    >
                      <div
                        className='text-base text-gray-500 dark:text-slate-400'
                        itemProp='text'
                      >
                        <MarkdownBlock
                          message={faq.answer.replace(/\n/g, '  \n')}
                        />
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
